import React from 'react';
import LegalPage from '../components/LegalPage';

// Source: WorldVibe Terms of Service (effective Aug 16, 2026).
// Kept in lockstep with the Privacy Policy (/privacy) and Child Safety
// Standards (/child-safety). When code changes, update all three together.
const CONTENT = `
**Effective Date:** August 16, 2026
**Last Updated:** August 16, 2026

## 1. Introduction

Welcome to WorldVibe ("WorldVibe," "we," "our," or "us"). WorldVibe is a global social discovery platform, operated by **SarvaRitam Businesses LLP**, that connects travelers through communities ("Planets"), real-time messaging, feed posts, a daily World Quest, virtual gifts, AI-assisted avatars, pets, ranking systems, dating features, and other social experiences.

By creating an account or using WorldVibe you agree to these Terms of Service.

## 2. Eligibility

- You must be **at least 13 years of age** to create an account. Users below 13 are refused at signup and any under-13 data attempted to be created is deleted immediately.
- If you are between 13 and 17 in a jurisdiction where a guardian's consent is required (India DPDP Act, Korea IIA, etc.), you represent that a guardian has agreed to these terms on your behalf.
- You may not use WorldVibe if you are barred from doing so under applicable law or if you have previously been banned from the platform.

## 3. Account Registration

- Provide accurate registration information, including a valid email or verified social identity (Google Sign-In / Sign in with Apple).
- Choose a strong password. You are responsible for maintaining its confidentiality.
- One account per person. You may not sell, transfer, or share access to your account.
- You must promptly notify us if you suspect unauthorised access.

## 4. Community Guidelines

WorldVibe is built around a positive global vibe. The following are prohibited:

**Illegal Content**
- Content that violates any law or regulation
- Fraud, scams, and misrepresentation
- Content that infringes third-party rights (copyright, trademark, right of publicity)

**Child Safety (zero-tolerance)**
- Child Sexual Abuse Material (CSAM), grooming, sextortion, or any content that sexually abuses or endangers a child (see our [Child Safety Standards](/child-safety))
- Confirmed reports are removed immediately, the account is terminated, and where required by law we may report the incident and preserve evidence for appropriate authorities

**Harmful Content**
- Content that promotes violence or terrorism
- Content that promotes self-harm or suicide
- Hate speech, discrimination based on protected characteristics
- Harassment, bullying, or targeted abuse

**Sexual & Explicit Content**
- Pornographic material
- Sexually explicit or gratuitous nudity
- Non-consensual intimate imagery

**Spam, Manipulation & Abuse**
- Spam or unsolicited commercial messaging
- Fake engagement (bought likes, coordinated inauthentic activity)
- Impersonation of individuals, brands, or WorldVibe staff
- Circumventing bans or moderation actions

**Platform Abuse**
- Distributing malware, phishing links, or malicious code
- Reverse-engineering, scraping, or automated data harvesting
- Attempts to disrupt or overload WorldVibe systems

## 5. User Content

You retain ownership of the content you post to WorldVibe. By posting content you grant WorldVibe a worldwide, royalty-free, non-exclusive licence to host, store, reproduce, display, distribute, and adapt that content for the purpose of operating and improving the service.

You are solely responsible for the content you create and confirm that it does not violate these Terms, applicable law, or the rights of third parties.

WorldVibe may — with or without notice — remove, restrict, or moderate content that violates these Terms or the Child Safety Standards.

## 6. Moderation & Enforcement

WorldVibe combines automated systems and human review to moderate content and behaviour:

- **Automated moderation**: text sent through DMs, planet chat, feed posts, and quest answers is scanned for CSAE terms, hate speech, and severe abuse. Content that matches the highest-severity filters is blocked at submission.
- **User reports & blocks**: every traveler can flag any user or piece of content via the report icon, and block any user (see §11 and §12).
- **Admin review queue**: flagged content lands in an admin queue prioritised by severity (CSAE > hate > spam).

Enforcement actions may include content removal, warnings, temporary restrictions, permanent bans, and — where confirmed illegal content is involved — preservation of evidence and reporting to appropriate authorities as required by law.

## 7. AI-Powered Features

WorldVibe includes AI-generated content:
- **AI cartoon avatar generation** (Gemini Nano Banana image model)
- **AI planet-host messages** (occasional short posts by Claude Sonnet)
- **Automated moderation** of user-generated text

AI-generated content is clearly labeled inside the app with an "AI" badge. The AI systems may produce inaccurate, incomplete, or unexpected results — you are responsible for reviewing AI-generated output before sharing it further. When you use these features you agree that the text you provide (the avatar prompt, or moderation-scanned text) is sent to the AI provider for processing.

## 8. Virtual Currency, Purchases & Refunds

- **Stars** are a premium currency you can buy with real money (via Razorpay on web, Google Play Billing on Android, or Apple StoreKit on iOS). Stars have no cash value, are non-transferable outside WorldVibe, and cannot be converted back to money.
- **Credits, XP, Cosmic Ranks, Pets, Gifts, and cosmetics** are internal digital licences with no monetary value and no expectation of resale.
- All purchases are final unless required to be refunded by applicable law or the app-store rules of the platform used to make the purchase (Google Play refund policy, Apple refund policy).
- WorldVibe may modify, balance, discontinue, or reset the virtual economy at any time.

## 9. Third-Party Services

WorldVibe integrates with:
- **Google Sign-In** (Google LLC) and **Sign in with Apple** (Apple Inc.) for authentication
- **Razorpay**, **Google Play Billing**, and **Apple StoreKit** for payments
- **Resend** for transactional emails
- **Emergent LLM Integrations** for AI features (Claude Sonnet, Gemini Nano Banana)
- **MongoDB Atlas** for database hosting
- **Emergent Cloud** for application hosting

You interact with these providers subject to their own terms and privacy policies.

## 10. Dating Features — **18+ Only**

WorldVibe's dating area (**Match Hearts** — swiping candidates, likes, matches, proposals, dating chats, and dating photos) is for legal adults **aged 18 or above**. Access is enforced server-side: every dating API is blocked for accounts under 18, and the dating UI is hidden from the app shell, home page, and menu for those accounts. If you are between 13 and 17 you can still use the rest of WorldVibe — planets, real-time chat, DMs, the feed, gifts, games, pets and the daily World Quest.

By using dating features you also understand:
- Only one "engaged" or "married" relationship is allowed at a time. Ending a relationship is unilateral — either partner can end it and the other is notified.
- You must interact respectfully. Harassment, unsolicited explicit content, or any attempt to solicit or contact a minor for sexual purposes is grounds for immediate ban **and reporting to authorities** in line with our Child Safety Standards.
- WorldVibe does not verify the real-world identity of other users. **Use caution when meeting anyone from the app in person.**

## 10a. Age Policy Summary

- **13+**: minimum age to create a WorldVibe account
- **18+**: minimum age to access any Dating feature (Match Hearts, proposals, marry, dating chats, dating photos)
- Under-13 accounts are refused at signup and any data mistakenly created is deleted immediately.
- Age is calculated server-side from the birthdate you enter at signup (or when Google/Apple accounts complete the age gate) and cannot be edited after the fact — that keeps age spoofing off the dating surface.

## 11. Reporting

If you encounter content that violates these Terms or the Child Safety Standards, please report it immediately using the flag icon on the user, message, DM, feed post, or quest response. Reports are triaged by severity; **CSAE reports are prioritised and emailed to our Child Safety Point of Contact (sarvaritam@gmail.com) immediately**.

## 12. Blocking

You can block any WorldVibe user from their profile. Blocked users can no longer message you and cannot see your profile activity in the feed. Blocking is silent — the other party is not notified. You can unblock any user from your settings.

## 13. Account Deletion

You can permanently delete your account at any time:
- **Inside the app**: Profile → Settings → Delete account (requires password or a typed confirmation phrase)
- **Public web form** (no login needed): [worldvibe.online/delete-account](/delete-account)

Upon deletion we permanently remove your account and personal data; messages you sent to other users are anonymised to "[message removed]" so the other party's chat history stays coherent. Backups may retain minimal audit metadata for up to 30 days.

## 14. Termination by WorldVibe

We may suspend or terminate accounts that violate these Terms, our Child Safety Standards, or applicable law. Where feasible we notify the account holder; where safety, legal, or investigative requirements apply we may act without prior notice.

## 15. Intellectual Property

The WorldVibe name, logo, cosmic branding, planet illustrations, pet illustrations, and interface designs are the property of SarvaRitam Businesses LLP and may not be copied, reproduced, or used without written permission (except as reasonable within-app screenshots for personal, non-commercial sharing).

## 16. Disclaimers

WorldVibe is provided **"as is" and "as available"** without warranties of any kind, whether express or implied, including without limitation implied warranties of merchantability, fitness for a particular purpose, and non-infringement.

We do not guarantee that the service will be uninterrupted, secure, or error-free, or that user-generated content will always be lawful, accurate, or safe.

## 17. Limitation of Liability

To the maximum extent permitted by law, WorldVibe and SarvaRitam Businesses LLP will not be liable for any indirect, incidental, special, consequential, or punitive damages — including loss of profits, data, goodwill, or virtual currency — arising out of or in connection with the service. Our total aggregate liability for any claim shall not exceed **the greater of ₹1,000 (INR) or the amount you paid us in the 12 months preceding the claim**.

## 18. Indemnification

You agree to indemnify and hold harmless WorldVibe and SarvaRitam Businesses LLP from any claims arising out of your use of the service, your violation of these Terms, or your violation of any third-party rights.

## 19. Governing Law

These Terms are governed by the laws of **India**, without regard to conflict-of-laws principles. Disputes shall be submitted to the courts located in **Ahmedabad, Gujarat, India**, subject to any consumer-protection laws that may grant you additional rights in your country of residence.

## 20. Changes to These Terms

We may update these Terms periodically. Material changes will be communicated via in-app notifications and/or email. Continued use of WorldVibe after an update constitutes acceptance of the revised Terms.

## 21. Contact

- **General support**: support@worldvibe.online
- **Security reports**: security@worldvibe.online
- **Child Safety Point of Contact**: sarvaritam@gmail.com (see [Child Safety Standards](/child-safety))
- **Data deletion (no login required)**: [worldvibe.online/delete-account](/delete-account)

**SarvaRitam Businesses LLP**
`;

export default function Terms() {
  return (
    <LegalPage
      title="Terms of Service"
      tagline="The rules of the WorldVibe cosmos"
      accent="#F472B6"
      content={CONTENT}
      backTo="/"
      testId="terms-page"
    />
  );
}
